// Heat is eligibility. Dish choices only personalize the serving suggestion.
function blacklandFlavorMatch(heat, flavor) {
  if (!['mild', 'bold'].includes(heat) || !['balanced', 'garlic', 'chile'].includes(flavor)) return null;
  if (heat === 'bold') {
    const reasons = {
      balanced: 'You asked for bold heat with savory depth. Spicy matches that heat. Original is the milder all-rounder; we have kept your heat preference first.',
      garlic: 'You asked for bold heat and richer aromatics. Spicy is the heat match. Garlic Shallot has the stronger garlic character, but it is the milder option.',
      chile: 'You chose bold heat and chile-forward flavor. Spicy brings both: layered chile character with a sustained finish.',
    };
    return { key: 'spicy', reason: reasons[flavor] };
  }
  if (flavor === 'garlic') return { key: 'garlic', reason: 'You chose mild warmth and richer aromatics. Garlic Shallot brings aromatic crunch while keeping the heat mild.' };
  return {
    key: 'original',
    reason: flavor === 'chile'
      ? 'You asked for chile-forward flavor but mild heat. Original is the closest mild match, with layered chile flavor. Spicy is more chile-forward, but its bolder heat is outside your choice.'
      : 'You chose mild warmth and balanced, savory flavor. Original is the versatile starting point, with layered flavor and savory depth.',
  };
}

function blacklandServingSuggestion(dish) {
  return {
    eggs: 'Start with a finishing spoonful over eggs, rice, or vegetables.',
    noodles: 'Try a finishing spoonful with noodles or dumplings.',
    grill: 'Start with a small spoonful over pizza or grilled food.',
  }[dish] || 'Stir the jar, add a little, and finish to your taste.';
}

class BlacklandFlavorFinder extends HTMLElement {
  #abort;
  #form;
  #panels;
  #invite;
  #status;
  #progress;
  #error;
  #resetButtons;
  #optional;
  #result;
  #motion;
  #mobile;
  #revealTimer;
  #resizeAnimation;
  #revealPending = false;
  #hasMatched = false;
  #matchedKey = null;

  connectedCallback() {
    this.#abort?.abort();
    this.#form = this.querySelector('[data-finder-form]');
    this.#panels = [...this.querySelectorAll('[data-finder-match]')];
    this.#invite = this.querySelector('[data-finder-invite]');
    this.#status = this.querySelector('[data-finder-status]');
    this.#progress = this.querySelector('[data-finder-progress]');
    this.#error = this.querySelector('[data-finder-error]');
    this.#resetButtons = [...this.querySelectorAll('[data-finder-reset]')];
    this.#optional = this.querySelector('[data-finder-optional]');
    const fallback = this.querySelector('[data-finder-fallback]');
    const result = this.#result = this.querySelector('[data-finder-result]');
    if (!this.#form || this.#panels.length !== 3 || !this.#invite || !this.#status || !this.#progress || !this.#error || !fallback || !result) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#mobile = window.matchMedia('(max-width: 749px)');
    this.#mobile.addEventListener('change', () => {
      this.#resizeAnimation?.cancel();
      if (this.#mobile.matches) this.#finishReveal();
    }, { signal });
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motion.addEventListener('change', () => {
      if (this.#motion.matches) {
        this.#resizeAnimation?.cancel();
        this.#finishReveal();
      }
    }, { signal });
    this.#form.reset();
    this.#resetState();
    this.#form.addEventListener('submit', this.#onSubmit, { signal });
    this.#form.addEventListener('change', this.#onChange, { signal });
    this.#form.addEventListener('reset', this.#onReset, { signal });
    for (const button of this.#resetButtons) {
      if (button.type === 'reset' && button.form === this.#form) continue;
      button.addEventListener('click', () => this.#form.reset(), { signal });
    }
    for (const panel of this.#panels) {
      panel.addEventListener('animationend', (event) => {
        if (event.target === panel) panel.classList.remove('is-revealing');
      }, { signal });
    }
    this.#form.hidden = false;
    fallback.hidden = true;
    result.hidden = false;
    this.setAttribute('data-enhanced', '');
    this.#status.textContent = '';
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#cancelReveal();
    this.#resizeAnimation?.cancel();
  }

  #resizeResult(previousHeight) {
    this.#resizeAnimation?.cancel();
    if (this.#mobile.matches) return;
    const nextHeight = this.#result.getBoundingClientRect().height;
    if (!previousHeight || Math.abs(nextHeight - previousHeight) < 1 || this.#motion.matches || !this.#result.animate) return;
    this.#resizeAnimation = this.#result.animate([
      { height: `${previousHeight}px` },
      { height: `${nextHeight}px` },
    ], { duration: 280, easing: 'ease-in-out' });
  }

  #cancelReveal() {
    clearTimeout(this.#revealTimer);
    this.#revealPending = false;
    this.#result?.classList.remove('is-revealing');
    this.#result?.removeAttribute('aria-busy');
  }

  #revealMatch(answers) {
    if (this.#hasMatched || this.#motion.matches || this.#mobile.matches) {
      this.#showMatch(answers, true);
      return;
    }
    if (this.#revealPending) return;
    this.#revealPending = true;
    this.#result.classList.add('is-revealing');
    this.#result.setAttribute('aria-busy', 'true');
    this.#revealTimer = setTimeout(() => this.#finishReveal(), 280);
  }

  #finishReveal() {
    const pending = this.#revealPending;
    this.#cancelReveal();
    if (pending && this.isConnected) this.#showMatch(this.#answers(), true);
  }

  #answers() {
    const data = new FormData(this.#form);
    return { heat: data.get('heat'), flavor: data.get('flavor'), dish: data.get('dish') };
  }

  #clearValidation() {
    this.#error.hidden = true;
    this.#error.textContent = '';
    for (const input of this.#form.querySelectorAll('[aria-invalid]')) {
      input.removeAttribute('aria-invalid');
      input.removeAttribute('aria-describedby');
    }
  }

  #updateProgress(answers) {
    this.#progress.textContent = answers.heat && answers.flavor
      ? (this.#hasMatched ? 'A match for your taste. Change a choice to explore.' : 'Your choices are in. Ready for your match.')
      : 'Two choices. A jar for your table.';
    const hasSelections = Boolean(answers.heat || answers.flavor || answers.dish);
    for (const button of this.#resetButtons) {
      button.hidden = this.#form.contains(button) ? !hasSelections || this.#hasMatched : !this.#hasMatched;
    }
  }

  #onSubmit = (event) => {
    event.preventDefault();
    const answers = this.#answers();
    this.#clearValidation();
    if (!blacklandFlavorMatch(answers.heat, answers.flavor)) {
      const missing = !answers.heat ? 'heat' : 'flavor';
      this.#error.textContent = !answers.heat && !answers.flavor ? 'Choose your heat and flavor preferences first.' : `Choose your ${missing} preference first.`;
      this.#error.hidden = false;
      const inputs = this.#form.querySelectorAll(`input[name="${missing}"]`);
      for (const input of inputs) {
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', this.#error.id);
      }
      inputs[0]?.focus();
      return;
    }
    this.#revealMatch(answers);
  };

  #onChange = () => {
    this.#cancelReveal();
    const answers = this.#answers();
    this.#clearValidation();
    this.#updateProgress(answers);
    if (this.#hasMatched) this.#showMatch(answers);
  };

  #showMatch(answers, focus = false) {
    const match = blacklandFlavorMatch(answers.heat, answers.flavor);
    if (!match) return;
    const selected = this.#panels.find((panel) => panel.dataset.finderMatch === match.key);
    if (!selected) return;
    const previousHeight = this.#result.getBoundingClientRect().height;
    const changedProduct = this.#matchedKey !== match.key;
    // Reveal a complete server-rendered product so its image, details and URL stay together.
    for (const panel of this.#panels) {
      panel.hidden = panel !== selected;
      if (changedProduct) panel.classList.remove('is-revealing');
    }
    selected.querySelector('[data-finder-reason]').textContent = match.reason;
    const serving = selected.querySelector('[data-finder-serving]');
    serving.textContent = blacklandServingSuggestion(answers.dish);
    serving.hidden = !answers.dish;
    this.#invite.hidden = true;
    this.#hasMatched = true;
    this.#matchedKey = match.key;
    this.setAttribute('data-has-match', '');
    this.#updateProgress(answers);
    if (changedProduct && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      selected.classList.add('is-revealing');
    }
    const heat = selected.dataset.productHeat ? ` ${selected.dataset.productHeat}.` : '';
    const availability = selected.dataset.productState === 'sold-out'
      ? ' Currently sold out.'
      : selected.dataset.productState === 'missing' ? ' Product details unavailable.' : '';
    const announcement = focus && this.#mobile.matches
      ? ''
      : `Your match: ${selected.dataset.productName}.${heat}${availability}`;
    if (this.#status.textContent !== announcement) this.#status.textContent = announcement;
    this.#resizeResult(previousHeight);
    if (focus) this.#focusElement(selected.querySelector('[data-finder-name]'));
  }

  #focusElement(element) {
    if (!element) return;
    element.focus({ preventScroll: true });
    // Reveal the whole question or matched jar while keeping semantic focus on the control or heading.
    const target = this.#mobile.matches && element.matches('[data-finder-name]')
      ? element
      : element.closest('fieldset, .bl-v2-finder__match-top') || element;
    const bounds = target.getBoundingClientRect();
    const styles = window.getComputedStyle(target);
    const headerHeight = parseFloat(window.getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    const focusStyles = window.getComputedStyle(element);
    const topInset = Math.max(parseFloat(styles.scrollMarginTop) || 0, parseFloat(focusStyles.scrollMarginTop) || 0, headerHeight + 16);
    const bottomInset = Math.max(parseFloat(styles.scrollMarginBottom) || 0, 24);
    const offset = bounds.top < topInset
      ? bounds.top - topInset
      : Math.max(0, bounds.bottom - window.innerHeight + bottomInset);
    if (offset) {
      window.scrollBy({
        top: offset,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
    }
  }

  #resetState() {
    const previousHeight = this.#result.getBoundingClientRect().height;
    this.#cancelReveal();
    this.#hasMatched = false;
    this.#matchedKey = null;
    this.removeAttribute('data-has-match');
    for (const panel of this.#panels) {
      panel.hidden = true;
      panel.classList.remove('is-revealing');
    }
    this.#invite.hidden = false;
    if (this.#optional) this.#optional.open = false;
    this.#clearValidation();
    this.#updateProgress({});
    this.#resizeResult(previousHeight);
  }

  #onReset = () => {
    this.#resetState();
    this.#status.textContent = 'Selections cleared. Choose your heat and flavor.';
    // Native reset restores the input values after its event handlers finish.
    queueMicrotask(() => this.#focusElement(this.#form.querySelector('input[name="heat"]')));
  };
}

if (!customElements.get('blackland-flavor-finder')) customElements.define('blackland-flavor-finder', BlacklandFlavorFinder);
